import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormControl, FormControlLabel, FormLabel, Grid, InputAdornment, Switch, TextField as MuiTextField } from '@mui/material';
import { compare, Interface } from 'verrific-plus-schema';
import { useIDKStyles, useStyles } from './TextField.style';
import { IDKSwitchProps, TextFieldImperative, TextFieldProps } from './TextField.type';
import { globalStyles } from '../Theme/ThemeProvider';
import { ScoreIcon } from '../Helpers/ScoreIcon';
import useStateWithPrevious from '../Utils/useStateWithPrevious.hook';

export const IDKSwitch = ({
  label,
  value,
  onChange
}: IDKSwitchProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.checked;
    onChange(val);
  };
  const classes = useIDKStyles();
  const defaultText = 'Disable field to skip';
  return <FormControlLabel className={classes.idkRoot} control={<Switch checked={value} onChange={handleChange}/>} label={label || defaultText} />;
};

const TextFieldImpl: React.ForwardRefRenderFunction<TextFieldImperative, TextFieldProps> = (
  {
    onChange,
    onBlur,
    value,
    disabled,
    size,
    error,
    label,
    maskRegex,
    multiline,
    required,
    prefix,
    suffix,
    placeHolder,
    style, 
    fieldStyle,
    type,
    maxValue,
    minValue,
    score,
    autoFocus,
    externalStyle,
    showIDK,
    onIDKChange,
    labelOfIDK,
    idkInitialValue,
    rows,
    centerLabel,
    id,
    ...rest
  }: TextFieldProps,
  ref
) => {
  const classes = useStyles();
  const [currentValue, setCurrentValue] = useState<string>(value || '');
  const [hasFocus, setFocused] = useState(false);
  const [hasError, setErrored] = useState(!!error);
  const [internalScore, setInternalScore] = useState<Interface.TScore>(score || 1);
  const globalClasses = globalStyles();
  const fieldRef = useRef(null);

  const [idkValue, setIdkValue, prevIdk] = useStateWithPrevious(idkInitialValue || false);
  
  const containerRef = useRef<any>(null);
  const spatialCaptureHandler = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (showIDK) {
      if (idkValue) {
        containerRef.current?.removeEventListener('keydown', spatialCaptureHandler, {capture: true});
      } else if (idkValue === false){
        containerRef.current?.addEventListener('keydown', spatialCaptureHandler, {capture: true});
      }
      return () => {
        containerRef.current?.removeEventListener('keydown', spatialCaptureHandler, {capture: true});
      };
    }
  }, [idkValue]);

  const handleIdkChange = (idkVal: boolean) => {
    setIdkValue(idkVal);
  };

  useEffect(() => {
    if (onIDKChange && prevIdk !== undefined) {
      onIDKChange(idkValue);
      if (onChange) {
        onChange(currentValue);
      }
    }
  }, [idkValue]);

  useImperativeHandle(ref, () => ({
    getLabel: () => (label || 'none'),
    getValue: () => String(currentValue),
    setValue: (newValue: string | number | boolean) => { setCurrentValue(String(newValue)); },
    setScore: (newScore: Interface.TScore) => {
      setInternalScore(newScore);
    },
    clearValue: () => { setCurrentValue(''); },
    isRequired: () => required || false,
    getFieldRef: () => fieldRef,
    isEnabled: () => idkValue,
    setEnabled: handleIdkChange
  }));

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: val } = e.target;
    const lastChar  = val[val.length-1];
    const floatAccepted = ((lastChar === '.' || val.length === 1 ) && (maskRegex && maskRegex.toString().includes('.\\d{1,2}')));

    if (
      val === '' || (
        floatAccepted && (
          val.includes(' ') ?
            Number(val.replaceAll(' ', '')) : 
            Number(val)
        )
      ) || (maskRegex && val.match(maskRegex) !== null) || !maskRegex
    ) {
      setCurrentValue(e.target.value);
      if (onChange) {
        onChange(e.target.value);
        if(!compare(e.target.value, value)){
          setInternalScore(1);
        }
      }
    }
  };
  const handleFocus = () => {
    setFocused(true);
    setErrored(false);
  };

  const handleBlur = () => {
    setFocused(false);
    if (onBlur) {
      onBlur(currentValue);
    }
  };

  useEffect(() => {
    if (error) {
      setErrored(true);
    }
  }, [error]);

  useEffect(() => {
    if(value !== undefined){
      setCurrentValue(value);
    } else {
      setCurrentValue('');
    }
  }, [value]);

  if (label) {
    return (
      <FormControl data-testid={rest['data-testid']} component="fieldset" className={globalClasses.standardMargin} fullWidth style={style} ref={containerRef}>
        <Grid container ref={fieldRef}>
          <Grid item md={12}>
            <FormLabel component="legend" sx={{display: 'flex', justifyContent: centerLabel ? 'center': 'normal'}} className={globalClasses.labelText}>
              {label} {required ? ' *' : null} { internalScore && ScoreIcon(internalScore) }
              {showIDK && <IDKSwitch label={labelOfIDK} value={idkValue} onChange={handleIdkChange} />}
            </FormLabel>
          </Grid>
          <Grid item md={12}>
            <MuiTextField
              autoFocus={autoFocus}
              placeholder={'Value'}
              className={`${multiline ? classes.textFieldFill :
                size === 'short' ? classes.textFieldShort :
                  size === 'large' ? classes.textField :
                    size === 'fill' ? classes.textFieldFill :
                      classes.textFieldShort
              } ${hasError ? classes.textFieldError : ''}
              ${prefix ? classes.textFieldPrefix : ''}
              ${suffix ? classes.textFieldSuffix : ''}
              ${multiline ? classes.multiline : ''}`}
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={currentValue}
              onChange={handleValueChange}
              type={type}
              disabled={disabled}
              error={hasError}
              id={id}
              style={fieldStyle}
              FormHelperTextProps={{className: size === 'short' ? classes.errorTextMessage : ''}}
              helperText={hasError ? error : ''}
              InputProps={{
                className: `${disabled ? classes.textFieldDisabled :
                  hasFocus ? classes.textFieldFocus :
                    ''}`,
                startAdornment: (
                  prefix ? <InputAdornment className={classes.suffixAndPrefix} position="start">
                    <p>{prefix}</p>
                  </InputAdornment> : null
                ),
                endAdornment: (
                  suffix ? <InputAdornment className={classes.suffixAndPrefix} position="start">
                    <p>{suffix}</p>
                  </InputAdornment> : null
                )
              }}
              multiline={multiline}
              minRows={multiline ? 3 : undefined}
              maxRows={multiline ? (rows || 8) : undefined}
            />
          </Grid>
        </Grid>
      </FormControl>
    );
  }
  return (
    <div ref={containerRef} data-testid={rest['data-testid']}>
      <MuiTextField
        ref={fieldRef}
        autoFocus={autoFocus}
        id={id}
        placeholder={placeHolder || 'Value'}
        className={`${multiline ? classes.textFieldFill :
          size === 'short' ? classes.textFieldShort :
            size === 'large' ? classes.textField :
              size === 'fill' ? classes.textFieldFill :
                classes.textFieldShort
        } ${hasError ? classes.textFieldError : ''} 
    ${prefix ? classes.textFieldPrefix : ''} 
    ${suffix ? classes.textFieldSuffix : ''}
    ${multiline ? classes.multiline : ''}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={currentValue}
        onChange={handleValueChange}
        disabled={disabled}
        error={hasError}
        style={fieldStyle}
        type={type}
        helperText={hasError ? error : ''}
        InputProps={{
          inputProps: {
            max: maxValue,
            min: minValue,
          },
          className: `${disabled ? classes.textFieldDisabled :
            hasFocus ? classes.textFieldFocus :
              ''} ${externalStyle}`,
          startAdornment: (
            prefix ? <InputAdornment className={classes.suffixAndPrefix} position="start">
              <p>{prefix}</p>
            </InputAdornment> : null
          ),
          endAdornment: (
            suffix ? <InputAdornment className={classes.suffixAndPrefix} position="start">
              <p>{suffix}</p>
            </InputAdornment> : null
          )
        }}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        maxRows={multiline ? 8 : undefined}
      />
    </div>
  );
};

export const TextField = React.forwardRef(TextFieldImpl);